import React from 'react';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Heading,
  Badge,
  Image,
} from '@chakra-ui/react';
import CalendlyButton from '../utils/CalendlyButton';
import UnderlineCTA from '../utils/UnderlineCTA.jsx';
import iconDemandeTitre from '../utils/images/icon_demande_titre.png';
import iconRenouvellementTitre from '../utils/images/icon_renouvellement_titre.png';
import iconVisa from '../utils/images/icon_visa.png';
import iconOQTF from '../utils/images/icon_OQTF.png';
import iconContestation from '../utils/images/icon-contestation.png';
import clipVideo from '../utils/videos/clip.mp4';

const servicesData = [
  {
    title: 'Première demande de titre de séjour',
    description: 'Accompagnement complet pour constituer et déposer votre première demande.',
    features: ['Vérification des pièces', 'Dépôt dématérialisé'],
    icon: iconDemandeTitre,
  },
  {
    title: 'Renouvellement de titre de séjour',
    description: 'Anticipez l\'expiration de votre titre et sécurisez votre renouvellement.',
    features: ['Analyse de la situation', 'Préparation du dossier', 'Dépôt sous 48h'],
    icon: iconRenouvellementTitre,
  },
  {
    title: 'Demande de visa',
    description: 'Constituez un dossier solide pour maximiser vos chances d\'obtention.',
    features: ['Liste personnalisée', 'Justificatifs optimisés', 'Assistance rendez-vous'],
    icon: iconVisa,
  },
  {
    title: 'OQTF',
    description: 'Recours contre une OQTF avec un avocat expérimenté.',
    features: ['Analyse de l\'arrêté', 'Stratégie de recours'],
    icon: iconOQTF,
  },
  {
    title: 'Contestation d\'une décision administrative',
    description: 'Défendez vos droits face à une décision défavorable.',
    features: ['Étude juridique', 'Rédaction des arguments', 'Suivi de la procédure'],
    icon: iconContestation,
  },
];

const ServicesSection = () => {
  return (
    <Box as="section" pt={0} pb={{ base: 14, lg: 20 }} bg="white" id="services">
      <Container maxW="1200px" px={5}>

        {/* Bande intro – vidéo à gauche, texte à droite (style iOS) */}
        <Box
          as="section"
          position="relative"
          left="50%"
          ml="-50vw"
          w="100vw"
          bg="linear-gradient(90deg, #0053b3, #ef3340)"
          py={{ base: 12, lg: 16 }}
          mb={{ base: 12, lg: 16 }}
        >
          <Container maxW="1200px" px={5}>
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 8, lg: 16 }} alignItems="center">
              <GridItem>
                <Box
                  as="video"
                  src={clipVideo}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  borderRadius="3xl"
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  boxShadow="0 25px 80px rgba(0,0,0,0.12)"
                  border="1px solid"
                  borderColor="gray.100"
                  sx={{ aspectRatio: '16 / 9' }}
                />
              </GridItem>
              <GridItem>
                <Box
                  bg="rgba(255,255,255,0.10)"
                  border="1px solid"
                  borderColor="whiteAlpha.300"
                  backdropFilter="blur(8px)"
                  borderRadius="2xl"
                  p={{ base: 5, lg: 7 }}
                >
                <VStack align="start" spacing={4} textAlign="left" color="white">
                  <Heading as="h2" size="xl" fontWeight={800} color="white">
                    Rester en France, le partenaire de confiance de vos démarches
                  </Heading>
                  <Text>
                    Obtenir ou renouveler un titre de séjour peut sembler complexe et décourageant.
                  </Text>
                  <Text>
                    Près de 8 étrangers sur 10 déclarent rencontrer des difficultés face aux démarches en ligne.
                  </Text>
                  <Text>
                    Avec Rester en France, vous bénéficiez d’un accompagnement clair, rapide et accessible, pour transformer une étape administrative stressante en une procédure simple et maîtrisée.
                  </Text>
                  <Box pt={2} w="full">
                    <UnderlineCTA
                      text="On vous en dit plus ?"
                      onClick={() => { window.location.href = '/a-propos'; }}
                      color="white"
                      underlineColor="whiteAlpha.800"
                      align="left"
                    />
                  </Box>
                </VStack>
                </Box>
              </GridItem>
            </Grid>
          </Container>
        </Box>
        <VStack spacing={6} textAlign="center" mb={{ base: 12, lg: 16 }}>
          <Box w="80px" h="8px" bg="linear-gradient(90deg, #0053b3, #ffffff, #ef3340)" borderRadius="full" />
          <Heading as="h2" size="2xl" fontWeight={800} letterSpacing="-0.02em" color="gray.900">
            Nos services clés
          </Heading>
        </VStack>

        {/* Ligne 1 : 2 services */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={{ base: 6, lg: 8 }} mb={8}>
          {servicesData.slice(0, 2).map((service, index) => (
            <GridItem key={index} display="flex">
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="0 8px 30px rgba(0,0,0,0.06)"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(0,0,0,0.10)' }}
                p={6}
                display="flex"
                flexDirection="column"
                h="100%"
                w="100%"
              >
                <VStack align="center" spacing={4} color="gray.900" flex="1" justify="space-between">
                  <Box
                    w="72px"
                    h="72px"
                    bg="white"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 8px 20px rgba(0,0,0,0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image src={service.icon} alt={service.title} w="40px" h="40px" />
                  </Box>
                  <Heading size="md" fontWeight={800} color="gray.900" textAlign="center">
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" textAlign="center">{service.description}</Text>
                  <VStack align="start" spacing={2}>
                    {service.features.map((feature, i) => (
                      <HStack key={i} spacing={2}>
                        <Text as="span" aria-hidden>•</Text>
                        <Text fontSize="sm"><Text as="b">{feature.split(':')[0]}</Text>{feature.includes(':') ? `:${feature.split(':').slice(1).join(':')}` : ''}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <HStack pt={2} justify="center" w="100%">
                    <Badge bg="#0053b3" color="white" borderRadius="full" px={4} py={2} fontWeight={800}>
                      {index === 0 ? 'Prix forfaitaire: 685€ TTC' : 'Prix forfaitaire: 585€ TTC'}
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            </GridItem>
          ))}
        </Grid>

        {/* Ligne 2 : 3 services */}
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={{ base: 6, lg: 8 }} mb={12}>
          {servicesData.slice(2).map((service, idx) => (
            <GridItem key={idx} display="flex">
              <Box
                bg="white"
                borderRadius="2xl"
                boxShadow="0 8px 30px rgba(0,0,0,0.06)"
                border="1px solid"
                borderColor="gray.100"
                transition="all 0.25s ease"
                _hover={{ transform: 'translateY(-6px)', boxShadow: '0 16px 40px rgba(0,0,0,0.10)' }}
                p={6}
                display="flex"
                flexDirection="column"
                h="100%"
                w="100%"
              >
                <VStack align="center" spacing={4} color="gray.900" flex="1" justify="space-between">
                  <Box
                    w="72px"
                    h="72px"
                    bg="white"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="gray.200"
                    boxShadow="0 8px 20px rgba(0,0,0,0.06)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Image src={service.icon} alt={service.title} w="40px" h="40px" />
                  </Box>
                  <Heading size="md" fontWeight={800} color="gray.900" textAlign="center">
                    {service.title}
                  </Heading>
                  <Text color="gray.600" fontSize="sm" textAlign="center">{service.description}</Text>
                  <VStack align="start" spacing={2}>
                    {service.features.map((feature, i) => (
                      <HStack key={i} spacing={2}>
                        <Text as="span" aria-hidden>•</Text>
                        <Text fontSize="sm"><Text as="b">{feature.split(':')[0]}</Text>{feature.includes(':') ? `:${feature.split(':').slice(1).join(':')}` : ''}</Text>
                      </HStack>
                    ))}
                  </VStack>
                  <HStack pt={2} justify="center" w="100%">
                    <Badge bg="#0053b3" color="white" borderRadius="full" px={4} py={2} fontWeight={800}>
                      Prix sur devis après consultation
                    </Badge>
                  </HStack>
                </VStack>
              </Box>
            </GridItem>
          ))}
        </Grid>

        <Box bg="linear-gradient(90deg, #0053b3, #ef3340)" borderRadius="2xl" p={{ base: 8, lg: 12 }} color="white" textAlign="center">
          <VStack spacing={5}>
            <Heading size="lg" fontWeight={800}>Besoin d’un accompagnement immédiat ?</Heading>
            <Text opacity={0.95}>Contactez-nous, un avocat vous rappelle sous 24h ouvrées.</Text>
            <HStack spacing={4} justify="center" flexWrap="wrap">
              <Button 
                size="lg" 
                bg="white" 
                color="#0053b3" 
                _hover={{ bg: 'gray.100' }} 
                fontWeight={700}
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Commencer maintenant
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                borderColor="white" 
                color="white" 
                _hover={{ bg: 'white', color: '#0053b3' }} 
                fontWeight={700}
                onClick={() => window.open('https://wa.me/33743641792', '_blank')}
              >
                Appeler
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;


